import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  InputAdornment,
  LinearProgress,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import { debounce, uniqueId } from 'lodash';
import { MRT_SortingState, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useModal } from 'mui-modal-provider';

import { venueActionAccessRequestApi } from '../../../api/accessRequest/venueActionAccessRequestApi';
import { useNotificationsContext } from '../../../contexts/NotificationsContext';
import {
  VennueAccessRequestItem,
  VenueAccessREnum,
  VenueListAccessRequetsFilter,
} from '../../../graphql/API';
import { useVenueListAccessRequestsCountQuery } from '../../../hooks/reactQuery/accessRequest/useVenueListAccessRequestsCountQuery';
import useAccessRequestQuery from '../../../hooks/reactQuery/useAccessRequestQuery';
import { AccessRequestPageView } from '../../../pages/AccessRequest/AccessRequest';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import {
  fetchAccountBalance,
  handleBalanceIsLoading,
} from '../../../redux/features/balance/balanceSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { delay } from '../../../utils/delay';
import { UserCategory, mapUserCategory } from '../../../utils/user';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { AccessRequestAlert, BulkAccessRequestAlert } from '../../Alerts/AccessRequestAlert';
import Button from '../../Button/Button';
import ChipOptions from '../../ChipOptions/ChipOptions';
import {
  AccessRequestAcceptedDialog,
  accessRequestPopupStorageKey,
} from '../../Dialogs/AccessRequestAccepted/AccessRequestAcceptedDialog';
import { AccessRequestConfirmationIncentiveDialog } from '../../Dialogs/AccessRequestConfirmationIncentive/AccessRequestConfirmationIncentiveDialog';
import {
  AccessRequestDeclineConfirmationDialog,
  accessRequestDeclineConfirmationStorageKey,
} from '../../Dialogs/AccessRequestDeclineConfirmation/AccessRequestDeclineConfirmationDialog';
import { BrandLimitReachedPopup } from '../../Dialogs/BrandLimitReached/BrandLimitReachedPopup';
import { OldRequestWarningDialog } from '../../Dialogs/OldRequestWarning/OldRequestWarningDialog';
import { ProductRequestsConfirmationDialog } from '../../Dialogs/ProductRequestsConfirmationDialog/ProductRequestsConfirmationDialog';
import { ProductRequestsRejectedDialog } from '../../Dialogs/ProductRequestsRejected/ProductRequestsRejectedDialog';
import { AccessRequestGrid } from '../../Grids/AccessRequest/AccessRequest';
import SvgIcon from '../../Icons';
import InfiniteScroll from '../../InfiniteScroll/InfiniteScroll';
import { useAccessRequestsColumn, useProductRequestsColumn } from './Column';
import { useAction } from './useAction';
import { mapToActionRequest } from './utils';

type AccessRequestTableProps = {
  view: AccessRequestPageView;
  height?: string;
  filters?: Partial<VenueListAccessRequetsFilter>;
  setFilters: React.Dispatch<
    React.SetStateAction<Partial<VenueListAccessRequetsFilter> | undefined>
  >;
  handleClickList: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  handleBlockedUsersList?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  setTotalRequests: React.Dispatch<React.SetStateAction<number>>;
  selectedOfferIds?: number[];
  isPromotionLimitHit?: boolean;
};

const AccessRequestTable: React.FC<AccessRequestTableProps> = ({
  view,
  height,
  filters,
  setFilters,
  handleClickList,
  setTotalRequests,
  selectedOfferIds = [],
  isPromotionLimitHit
}) => {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const appDispatch = useAppDispatch();

  const venueState = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) || false;
  const isBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans);

  const { subscription } = useAppSelector((state) => state.subscription.value);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const queryClient = useQueryClient();

  const [dontShowAccessRequestPopup, setDontShowAccessRequestPopup] = React.useState(false);
  const [productRequestsRejectPopup, setProductRequestsRejectPopup] = React.useState(false);

  const [rejectedAccessRequests, setRejectedAccessRequests] =
    React.useState<VennueAccessRequestItem | null>(null);

  const { refetchReservationsHandler, handleUpdateAccessRequest } = useNotificationsContext() || {};
  const [sorting, setSorting] = React.useState<MRT_SortingState>([
    { id: filters?.sortBy ?? 'createdAt', desc: filters?.sortDirection === 'desc' },
  ]);
  const [selectedRows, setSelectedRows] = React.useState<Record<string, boolean>>({});
  const [isBulkUpdatingStarted, setIsBulkUpdatingStarted] = React.useState(false);
  const [isBulkRequestsCompleted, setIsBulkRequestsCompleted] = React.useState(false);

  const [bulkOperationConfirmationState, setBulkOperationConfirmationState] = React.useState<{
    popup: boolean;
    state: 'accept' | 'reject';
  }>({ popup: false, state: 'accept' });

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useAccessRequestQuery(filters);
  const { data: userCategoriesCountsData } = useVenueListAccessRequestsCountQuery({
    status: filters?.status,
    searchText: filters?.searchText,
  });

  const filteredData = React.useMemo(() => {
    const validData = (data ?? []).filter(Boolean);
    if (selectedOfferIds.length === 0) return validData;
    return validData.filter((request) =>
      request.offerID !== null &&
      request.offerID !== undefined &&
      selectedOfferIds.includes(request.offerID)
    );
  }, [data, selectedOfferIds]);

  const actionProps = useAction();

  const [selectedAccessRequest, setSelectedAccessRequest] =
    React.useState<VennueAccessRequestItem | null>(null);

  const [incentiveDialog, setIncentiveDialog] = React.useState(false);
  const [showDeclineConfirmationDialog, setShowDeclineConfirmationDialog] = React.useState(false);
  const [selectedDeclineAccessRequest, setSelectedDeclineAccessRequest] =
    React.useState<VennueAccessRequestItem | null>(null);

  const [oldRequestWarningOpen, setOldRequestWarningOpen] = React.useState(false);
  const [pendingOldAcceptRequest, setPendingOldAcceptRequest] =
    React.useState<VennueAccessRequestItem | null>(null);

  const accessRequestsColumn = useAccessRequestsColumn({
    ...actionProps,
    onAccept: (accessRequest) => {
      setSelectedAccessRequest(accessRequest);
    },
  });

  const productRequestsColumn = useProductRequestsColumn({
    ...actionProps,
    isBulkActionStarted: Object.values(selectedRows).length > 0 && isBulkUpdatingStarted,
    onAccept: (accessRequest) => {
      setSelectedAccessRequest(accessRequest);
    },
    // Only show ProductRequestsRejectedDialog in grid view (when view is not 'list')
    // In list view, each request is shown separately, so we don't need this dialog
    onReject: view !== 'list' ? (productRequest) => {
      setProductRequestsRejectPopup(true);
      setRejectedAccessRequests(productRequest);
    } : undefined,
    onRejectWithConfirmation: (accessRequest) => {
      const dontShowValue = localStorage.getItem(
        accessRequestDeclineConfirmationStorageKey + String(venueState?.id),
      );
      const shouldSkipConfirmation = dontShowValue ? JSON.parse(dontShowValue) : false;

      if (shouldSkipConfirmation) {
        actionProps.onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined));
      } else {
        setSelectedDeclineAccessRequest(accessRequest);
        setShowDeclineConfirmationDialog(true);
      }
    },
    onAcceptWithIncentive: (accessRequest) => {
      setSelectedAccessRequest(accessRequest);
      setIncentiveDialog(true);
    },
    onAcceptOldRequest: (accessRequest) => {
      setPendingOldAcceptRequest(accessRequest);
      setOldRequestWarningOpen(true);
    },
  });

  const handleOldRequestConfirm = () => {
    const accessRequest = pendingOldAcceptRequest;
    setOldRequestWarningOpen(false);
    setPendingOldAcceptRequest(null);
    if (!accessRequest) return;

    if ((accessRequest.offer?.incentiveValue || 0) > 0) {
      setSelectedAccessRequest(accessRequest);
      setIncentiveDialog(true);
    } else {
      actionProps.onAction(mapToActionRequest(accessRequest, VenueAccessREnum.accepted));
      setSelectedAccessRequest(accessRequest);
    }
  };

  const handleOldRequestClose = () => {
    setOldRequestWarningOpen(false);
    setPendingOldAcceptRequest(null);
  };

  const bulkAccessRequestsAction = async (action: VenueAccessREnum) => {
    if (isBrand && !subscriptionPlan) {
      return navigate('/plans');
    }
    try {
      const selectedRowsKeys = Object.keys(selectedRows);
      const selectedRowsData = data?.filter((item) =>
        selectedRowsKeys.includes(`${item.userID}-${item.offerID}`),
      );

      setIsBulkUpdatingStarted(true);

      if (selectedRowsData) {
        await Promise.all(
          selectedRowsData.map((item) => {
            if (!item.userID || !item.offerID) {
              return;
            }
            return venueActionAccessRequestApi({
              action: action as VenueAccessREnum,
              userID: item.userID,
              offerID: item.offerID,
            });
          }),
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['list-access-request'] });

      setIsBulkUpdatingStarted(false);
      setSelectedRows({});
      setIsBulkRequestsCompleted(true);
    } catch (error) {
      const _error = error as Error;
      if (_error.message.includes('limit')) {
        showModal(BrandLimitReachedPopup, {});
      }
      setIsBulkUpdatingStarted(false);
      setSelectedRows({});
      setIsBulkRequestsCompleted(false);
      console.error('Error performing bulk action:', error);
    }
  };

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (scrollHeight - scrollTop - clientHeight < 500 && !isFetching && hasNextPage) {
          fetchNextPage();
        }
      }
    },
    [isFetching, fetchNextPage, hasNextPage],
  );

  const renderTopToolBar = React.useCallback(
    () => !isLoading && isFetching && <LinearProgress />,
    [isLoading, isFetching],
  );

  const handleAccessRequestSuccessPopup = () => {
    const showAccessRequestPopupValue = localStorage.getItem(accessRequestPopupStorageKey);
    const shouldShow = showAccessRequestPopupValue
      ? JSON.parse(showAccessRequestPopupValue)
      : false;
    setDontShowAccessRequestPopup(shouldShow);
  };

  const handleRefetchVenueBalance = React.useCallback(() => {
    appDispatch(handleBalanceIsLoading());
    delay(5000).then(() => {
      appDispatch(fetchAccountBalance({ venueID: venueState?.id || '' }));
    });
  }, [appDispatch, venueState?.id]);

  React.useEffect(() => {
    setTotalRequests(userCategoriesCountsData?.totalAll || 0);
  }, [userCategoriesCountsData]);

  React.useEffect(() => {
    if (data.length > 0 && handleUpdateAccessRequest) {
      handleUpdateAccessRequest(data[0].createdAt || '');
    }
  }, [data]);

  React.useEffect(() => {
    handleAccessRequestSuccessPopup();
  }, []);

  React.useEffect(() => {
    if (actionProps.successState && isOnlineBrand) {
      if (refetchReservationsHandler) refetchReservationsHandler();
    }
  }, [actionProps.successState, isOnlineBrand, refetchReservationsHandler]);

  React.useEffect(() => {
    const [sort] = sorting;
    setFilters((x) => ({
      ...x,
      sortBy: sort?.id || 'createdAt',
      sortDirection: sort?.desc ? 'desc' : 'asc',
    }));
  }, [sorting]);

  React.useEffect(() => {
    if (!!selectedAccessRequest?.offer?.incentiveValue && actionProps.successState) {
      handleRefetchVenueBalance();
    }
  }, [actionProps.successState, selectedAccessRequest]);

  const table = useMaterialReactTable({
    enableRowSelection: isOnlineBrand || false,
    enableStickyHeader: true,
    enableTopToolbar: false,
    mrtTheme: { selectedRowBackgroundColor: '#F6F6F6' },
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
    ...(isOnlineBrand ? { onRowSelectionChange: setSelectedRows } : {}),
    muiTableHeadProps: { sx: { opacity: 1 } },
    muiTableHeadRowProps: {
      sx: { backgroundColor: 'white', boxShadow: 'none' },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.getIsSelected() ? '#F6F6F6' : 'inherit',
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        color: 'text.secondary',
        borderColor: custom.colors.brandLightGrey,
        fontWeight: 400,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    muiTableContainerProps: {
      sx: { height, maxHeight: height, backgroundColor: 'white' },
      onScroll: (event) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    muiFilterCheckboxProps: {
      color: 'secondary',
    },
    muiSelectCheckboxProps: {
      sx: {
        color: '#E0E0E0',
        '&.Mui-checked': {
          color: '#000',
        },
      },
    },
    renderBottomToolbar: renderTopToolBar,
    columns: isOnlineBrand ? productRequestsColumn : accessRequestsColumn,
    data: filteredData,
    rowVirtualizerOptions: { overscan: 5 },
    state: { sorting, isLoading, ...(isOnlineBrand ? { rowSelection: selectedRows } : {}) },
    renderEmptyRowsFallback: () => {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component='img'
            src='/png/access-empty@2x.png'
            sx={{ marginTop: 10, marginBottom: 2, width: '100px' }}
          />
          <Typography fontWeight='medium'>No access requests yet</Typography>
          <Typography variant='caption'>
            When users request access to your {isOnlineBrand ? 'products' : 'offers'}, you’ll see
            them listed here
          </Typography>
        </Box>
      );
    },
    getRowId: (row) =>
      row?.userID && row?.offerID ? `${row.userID}-${row.offerID}` : uniqueId('product-requests'),
  });

  const handleProductRequestsRejectedPopup = () => {
    setProductRequestsRejectPopup((prev) => !prev);
  };

  const handleProductRequestsConfirmationDialog = () => {
    setBulkOperationConfirmationState((prev) => ({ ...prev, popup: !prev.popup }));
  };

  const handleSearch = debounce((value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchText: value,
    }));
  }, 500);

  const handleChange = React.useCallback(
    (value?: string) => {
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleIncentiveDialog = () => {
    setIncentiveDialog((prev) => !prev);
  };

  const onIncentiveConfirmHandle = () => {
    if (selectedAccessRequest) {
      handleIncentiveDialog();
      actionProps.onAction(
        mapToActionRequest(selectedAccessRequest ?? undefined, VenueAccessREnum.accepted),
      );
      handleAccessRequestSuccessPopup();
    }
  };

  const handleCloseDeclineConfirmationDialog = () => {
    setShowDeclineConfirmationDialog(false);
    setSelectedDeclineAccessRequest(null);
  };

  const handleDeclineConfirmation = React.useCallback(
    (accessRequest: VennueAccessRequestItem | null) => {
      setShowDeclineConfirmationDialog(false);
      setSelectedDeclineAccessRequest(null);
      if (accessRequest) {
        actionProps.onAction(
          mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined),
        );
      }
    },
    [actionProps],
  );

  const userCategoryOptions = [
    { label: `All (${userCategoriesCountsData?.totalAll || 0})`, value: '' },
    {
      value: 'true',
      label: `${mapUserCategory[UserCategory.lifestyle]} (${
        userCategoriesCountsData?.totalBPLifestyle || 0
      })`,
    },
    {
      value: 'false',
      label: `${mapUserCategory[UserCategory.select]} (${
        userCategoriesCountsData?.totalBPSelect || 0
      })`,
    },
  ];

  const totalSelectedRows = React.useMemo(() => Object.values(selectedRows).length, [selectedRows]);

  const uniqueUsersForGrid = React.useMemo(() => {
    const userMap = new Map<number, VennueAccessRequestItem>();

    for (const request of data) {
      const userID = request.userID;
      if (userID && !userMap.has(userID)) {
        userMap.set(userID, request);
      }
    }

    const uniqueAccessRequests = Array.from(userMap.values());
    return uniqueAccessRequests;
  }, [data]);

  return (
    <Box position={'relative'}>
      <Stack spacing={isOnlineBrand ? 0 : 2}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems={'flex-start'}
          sx={{ paddingY: '8px' }}
        >
          {!isOnlineBrand ? (
            <ChipOptions
              name='userCategory'
              options={userCategoryOptions}
              onChange={({ value: v }) => {
                setFilters((prevFilters) => {
                  const lifestyleValue = !v ? undefined : v === 'true' ? true : false;
                  return { ...prevFilters, lifestyle: lifestyleValue };
                });
              }}
            />
          ) : (
            <div></div>
          )}
          {isOnlineBrand ? (
            <></>
          ) : (
            <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
              <OutlinedInput
                id='outlined-adornment-amount'
                placeholder='Search a member...'
                startAdornment={
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                }
                onChange={(e) => {
                  handleChange(e.target.value);
                }}
                size='small'
                sx={{
                  paddingY: '1px',
                }}
              />
              <Button
                id='btn-access-list'
                variant='outlined'
                size='small'
                startIcon={<SvgIcon name='InviteSent' sx={{ fontSize: '20px' }} />}
                onClick={handleClickList}
                sx={{
                  paddingY: '8.7px',
                }}
              >
                Access list
              </Button>
            </Box>
          )}
        </Box>

        {view === 'list' ? (
          <MaterialReactTable table={table} />
        ) : (
          <Box
            sx={{
              overflowY: 'auto',
              height: height,
              maxHeight: height,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              msOverflowStyle: 'none',
            }}
          >
            <InfiniteScroll
              isLoading={isLoading}
              isFetching={isFetching}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
            >
              <AccessRequestGrid data={uniqueUsersForGrid} isLoading={isLoading || isFetching} isPromotionLimitHit={isPromotionLimitHit}  />
            </InfiniteScroll>
          </Box>
        )}
        <AccessRequestAlert
          open={
            ((selectedAccessRequest || false) && actionProps.successState && isOnlineBrand) || false
          }
          onClose={() => actionProps.setSuccessState(false)}
          accessRequest={selectedAccessRequest}
        />
        <BulkAccessRequestAlert
          open={isBulkRequestsCompleted}
          onClose={() => setIsBulkRequestsCompleted(false)}
        />
        {selectedAccessRequest && !dontShowAccessRequestPopup && (
          <AccessRequestAcceptedDialog
            open={
              ((selectedAccessRequest || false) &&
                actionProps.successState &&
                isOnlineBrand &&
                !dontShowAccessRequestPopup) ||
              false
            }
            accessRequest={selectedAccessRequest}
            reservationID={actionProps.reservationID}
            onClose={() => {
              setSelectedAccessRequest(null);
            }}
          />
        )}
      </Stack>
      {totalSelectedRows > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '2.5%',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Button
            variant='contained'
            sx={{
              borderRadius: '8px',
              border: '1.5px solid #000',
              padding: '8px 20px',
              backgroundColor: 'white',
              color: '#000',
            }}
            onClick={() => {
              setBulkOperationConfirmationState((prev) => ({
                ...prev,
                popup: true,
                state: 'accept',
              }));
            }}
            disabled={isBulkUpdatingStarted}
          >
            Accept selected ({totalSelectedRows})
          </Button>
          <Button
            variant='contained'
            sx={{
              borderRadius: '8px',
              border: '1.5px solid #000',
              padding: '8px 20px',
              backgroundColor: 'white',
              color: '#000',
            }}
            onClick={() => {
              setBulkOperationConfirmationState((prev) => ({
                ...prev,
                popup: true,
                state: 'reject',
              }));
            }}
            disabled={isBulkUpdatingStarted}
          >
            Reject selected ({totalSelectedRows})
          </Button>
        </Box>
      )}

      <ProductRequestsRejectedDialog
        open={productRequestsRejectPopup}
        accessRequest={rejectedAccessRequests}
        onClose={handleProductRequestsRejectedPopup}
      />

      <ProductRequestsConfirmationDialog
        state={bulkOperationConfirmationState.state}
        open={bulkOperationConfirmationState.popup}
        onClose={handleProductRequestsConfirmationDialog}
        selectedUsers={totalSelectedRows}
        onAccept={() => {
          bulkAccessRequestsAction(
            bulkOperationConfirmationState.state === 'accept'
              ? VenueAccessREnum.accepted
              : VenueAccessREnum.declined,
          );
        }}
      />

      <AccessRequestConfirmationIncentiveDialog
        open={incentiveDialog}
        onClose={handleIncentiveDialog}
        accessRequest={selectedAccessRequest}
        onConfirm={onIncentiveConfirmHandle}
      />

      <AccessRequestDeclineConfirmationDialog
        open={showDeclineConfirmationDialog}
        onClose={handleCloseDeclineConfirmationDialog}
        accessRequest={selectedDeclineAccessRequest}
        onDecline={handleDeclineConfirmation}
        venueID={String(venueState?.id)}
      />

      <OldRequestWarningDialog
        open={oldRequestWarningOpen}
        onClose={handleOldRequestClose}
        onConfirm={handleOldRequestConfirm}
        accessRequest={pendingOldAcceptRequest}
      />
    </Box>
  );
};

export default AccessRequestTable;
